"use client";

// ConnectCards — what the connect walk draws inside netti's conversation.
//
// A netti line in the rail can carry a card (NettiChat ChatCard). The card holds
// only a reference (the walk, the step); it reads the walk's live state from the
// rail's context, so a call landing or a name changing redraws the card and
// nothing else. The NEWEST card of a walk, on the step the walk is on, is the
// live one. The last card of a step the walk has passed settles into one quiet
// line saying how that step went; every older card of a step says nothing (its
// netti line above it is the record).
//
// John, 2026-10-27, second pass: "a very simple, intuitive, holistic experience",
// "too many underlined links and buttons". So a card is the words netti said, the
// one real screen for that step, the one thing to take (THE link bar, or a block
// with THE copy disc), and ONE button. Everything else a person might want ("show
// me", "it isn't showing up", "skip") is a reply netti offers above the composer,
// or just typed. Nothing scrolls sideways.

import * as React from "react";
import BrandIcon from "../../BrandIcon";
import CopyBlock from "../../CopyBlock";
import { PillButton } from "../../ui";
import { StateDot } from "../../connect/MintModal";
import { LinkBar } from "../../skills/PasteUrl";
import MediaModal from "../../company/MediaModal";
import type { ChatCard } from "../NettiChat";
import { connState, type Connection } from "../../../lib/connect/connections";
import { MCP_URL, PROMPT_SYNC, type ConnectTool, type GuideShot } from "../../../lib/connect/tools";
import {
  NPX_COMMAND,
  SLOW_AFTER_MS,
  STEP_WORD,
  appWord,
  handSteps,
  pickable,
  pluginOf,
  stepsFor,
  suggestNext,
  toolWord,
  tryItAsk,
  waysFor,
  walkTool,
  type Walk,
  type WalkStep,
  type WalkWay,
} from "../../../lib/connect/walk";
import { wiresItself } from "../../../lib/connect/setup";
import { relTime } from "../../../lib/os/tree";
import { copyText } from "../../../lib/clipboard";
import { trackFunnel, EVENTS } from "../../../lib/analytics";
import { NETTI_UI_EVENT } from "../NettiUiBridge";
import type { ConnectWalks } from "./useConnectWalks";
import "./connect-rail.css";

const FLOW = "netti_walk";

export interface ConnectRail {
  ctl: ConnectWalks;
  /** each walk's newest card message id */
  latest: Record<string, string>;
  /** each walk's newest card per step (`walk:step`): the one that settles into a line */
  latestStep: Record<string, string>;
}

export const ConnectRailContext = React.createContext<ConnectRail | null>(null);
const useRail = () => React.useContext(ConnectRailContext);

/** NettiChat's renderCard for the rail */
export function renderConnectCard(card: ChatCard, msgId: string): React.ReactNode {
  return <ConnectCard card={card} msgId={msgId} />;
}

function ConnectCard({ card, msgId }: { card: ChatCard; msgId: string }): React.JSX.Element | null {
  const rail = useRail();
  if (!rail) return null;
  if (card.kind === "connect-pick") return rail.latest["pick"] === msgId || !rail.latest["pick"] ? <PickCard /> : null;
  const w = rail.ctl.walks.find((x) => x.id === card.walk);
  const t = w ? walkTool(w.tool) : null;
  if (!w || !t) return null;
  const step = (card.step as WalkStep) ?? "wire";
  if (rail.latest[w.id] === msgId && w.step === step) return <LiveCard w={w} t={t} step={step} />;
  if (rail.latestStep[`${w.id}:${step}`] === msgId && w.step !== step) return <PastLine w={w} t={t} step={step} conn={rail.ctl.connOf(w)} />;
  return null;
}

// ── a step that has passed: one quiet line ────────────────────────────────

function PastLine({ w, t, step, conn }: { w: Walk; t: ConnectTool; step: WalkStep; conn: Connection | null }) {
  const skipped = w.skipped.includes(step);
  let said: string;
  let ok = !skipped;
  if (step === "wire") {
    ok = !!w.arrived || !!conn?.last_seen_at;
    said = ok ? `${w.label} called` : "still listening for its first call";
  } else if (step === "instructions") {
    said = w.heard ? "it reached for your memory on its own" : skipped ? "skipped for now" : "pasted";
  } else if (step === "trust") {
    said = skipped ? "skipped for now" : "it works without asking";
  } else if (step === "import") {
    said = skipped ? "later" : "brought in";
  } else {
    said = `${toolWord(t)} is in`;
  }
  return (
    <p className="ncx-past" data-ok={ok || undefined}>
      {ok ? <Tick /> : <span className="ncx-dash" aria-hidden>–</span>}
      <span className="ncx-past__step">{STEP_WORD[step]}</span>
      <span>{said}</span>
    </p>
  );
}

// ── the live card ─────────────────────────────────────────────────────────

function LiveCard({ w, t, step }: { w: Walk; t: ConnectTool; step: WalkStep }) {
  const rail = useRail()!;
  const conn = rail.ctl.connOf(w);
  return (
    <section className="ncx-card" data-step={step} aria-label={`connecting ${toolWord(t)}: ${STEP_WORD[step]}`}>
      <CardHead w={w} t={t} conn={conn} />
      {step === "wire" && <WireBody w={w} t={t} conn={conn} />}
      {step === "instructions" && <InstructionBody w={w} t={t} conn={conn} />}
      {step === "trust" && <TrustBody w={w} t={t} />}
      {step === "import" && <ImportBody w={w} t={t} />}
      {step === "done" && <DoneBody w={w} t={t} />}
    </section>
  );
}

/** the tool's mark, its name, the connection's name (an edit in place), its state */
function CardHead({ w, t, conn }: { w: Walk; t: ConnectTool; conn: Connection | null }) {
  const rail = useRail()!;
  const state = conn ? connState(conn, rail.ctl.now) : "waiting";
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(w.label);
  const save = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== w.label) await rail.ctl.rename(w.id, next);
  };
  return (
    <header className="ncx-head">
      <BrandIcon name={t.icon_key} size={22} className="ncx-head__mark" />
      <span className="ncx-head__who">
        <span className="ncx-head__tool">{toolWord(t)}</span>
        {editing ? (
          <input
            className="input-soft ncx-name-input"
            value={draft}
            maxLength={80}
            autoFocus
            aria-label="what to call this connection"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void save()}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === "Enter") { e.preventDefault(); void save(); }
              if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setDraft(w.label); setEditing(false); }
            }}
          />
        ) : (
          <button type="button" className="ncx-name" onClick={() => { setDraft(w.label); setEditing(true); }} aria-label={`rename ${w.label}`}>
            <span>{w.label}</span>
            <Pencil />
          </button>
        )}
      </span>
      <span className="ncx-state" title={state === "waiting" ? "waiting for its first call" : state}>
        <StateDot state={state} />
        <span>{state}</span>
      </span>
    </header>
  );
}

// ── connect: the way in, then one step at a time ──────────────────────────

const WAY_WORD = (t: ConnectTool, way: WalkWay, installed: boolean): string =>
  way === "plugin" ? "add the innernet plugin" : way === "npx" ? "one command" : way === "coach" ? (installed ? "let the extension do it" : "add the extension") : "walk me through it";

function WireBody({ w, t, conn }: { w: Walk; t: ConnectTool; conn: Connection | null }) {
  const rail = useRail()!;
  const { ctl } = rail;
  if (!w.way) {
    const installed = ctl.coach.can(t.id);
    // the extension leads only once it is in this browser; until then walking it by hand comes first
    const offered = waysFor(t, { coach: ctl.coach.offered || installed });
    const ways = installed ? offered : [...offered.filter((x) => x !== "coach"), ...offered.filter((x) => x === "coach")];
    return (
      <div className="ncx-body">
        <div className="ncx-ways" role="group" aria-label="how to connect it">
          {ways.map((way, i) => (
            <button key={way} type="button" className="ncx-way" data-first={i === 0 || undefined} onClick={() => ctl.choose(w.id, way)}>
              <WayMark way={way} />
              <span>{WAY_WORD(t, way, installed)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (w.way === "plugin") return <PluginBody w={w} t={t} conn={conn} />;
  if (w.way === "npx") {
    return (
      <div className="ncx-body">
        <CopyBlock text={NPX_COMMAND} className="copy-block--soft ncx-block" />
        <LiveCheck w={w} t={t} conn={conn} />
      </div>
    );
  }
  if (w.way === "coach") {
    return (
      <div className="ncx-body">
        <LiveCheck w={w} t={t} conn={conn} />
      </div>
    );
  }
  const steps = handSteps(t);
  const at = Math.min(w.at, steps.length);
  if (at >= steps.length) {
    const last = steps[steps.length - 1];
    return (
      <div className="ncx-body">
        {last?.shot && <Shot shot={last.shot} t={t} />}
        <LiveCheck w={w} t={t} conn={conn} />
      </div>
    );
  }
  const step = steps[at];
  const isLast = at === steps.length - 1;
  return (
    <div className="ncx-body">
      {steps.length > 1 && <p className="ncx-count">{at + 1} of {steps.length}</p>}
      {step.shot && <Shot shot={step.shot} t={t} />}
      {step.paste === "link" && <LinkCopy url={MCP_URL} />}
      {(step.paste === "block" || step.paste === "command") && (
        <>
          <CopyBlock text={t.config} className="copy-block--soft ncx-block" />
          {t.where && <p className="ncx-where">it goes in {t.where}</p>}
        </>
      )}
      {isLast && <LiveCheck w={w} t={t} conn={conn} />}
      {step.deeplink ? (
        <div className="ncx-act">
          <PillButton as="a" href={step.deeplink} variant="primary" style={{ width: "auto" }}
            onClick={() => { trackFunnel(EVENTS.connect_instructions_copied, { what: "deeplink", flow: FLOW }); setTimeout(() => ctl.stepDone(w.id), 400); }}>
            open in {t.name}
          </PillButton>
        </div>
      ) : !isLast ? (
        <div className="ncx-act">
          <PillButton type="button" variant="primary" style={{ width: "auto" }} onClick={() => ctl.stepDone(w.id)}>done</PillButton>
        </div>
      ) : null}
    </div>
  );
}

/** THE PLUGIN: one press to its page in ChatGPT, the live check under it. For a tool `npx innernet` also
 *  wires (Codex), the command waits quietly below, never as a second choice. */
function PluginBody({ w, t, conn }: { w: Walk; t: ConnectTool; conn: Connection | null }) {
  const href = pluginOf(t);
  return (
    <div className="ncx-body">
      {href && (
        <div className="ncx-act">
          <PillButton as="a" href={href} target="_blank" rel="noopener noreferrer" variant="primary" style={{ width: "auto" }} data-way="plugin"
            onClick={() => trackFunnel(EVENTS.connect_instructions_copied, { what: "plugin", flow: FLOW, client: t.id })}>
            add the innernet plugin
          </PillButton>
        </div>
      )}
      <p className="ncx-where">opens in ChatGPT. press Connect, then sign in to innernet.</p>
      <LiveCheck w={w} t={t} conn={conn} />
      {wiresItself(t.id) && (
        <details className="ncx-alt">
          <summary>or from a terminal</summary>
          <CopyBlock text={NPX_COMMAND} className="copy-block--soft ncx-block" />
        </details>
      )}
    </div>
  );
}

/** THE LIVE CHECK: the row, read every few seconds while it waits */
function LiveCheck({ w, t, conn }: { w: Walk; t: ConnectTool; conn: Connection | null }) {
  const rail = useRail()!;
  const making = rail.ctl.making[w.id];
  const heard = !!conn?.last_seen_at || !!w.arrived;
  if (making === "failed" && !w.conn) {
    return (
      <div className="ncx-live" data-state="failed" role="status">
        <span className="conn-dot" data-state="quiet" aria-hidden />
        <span>the connection didn&rsquo;t get made just now.</span>
        <PillButton type="button" style={{ width: "auto" }} onClick={() => rail.ctl.retryMake(w.id)}>try again</PillButton>
      </div>
    );
  }
  if (heard) {
    return (
      <p className="ncx-live" data-state="heard" role="status">
        <StateDot state="live" />
        <span>{toolWord(t)} called {conn?.last_seen_at ? relTime(conn.last_seen_at) : "just now"}</span>
      </p>
    );
  }
  const slow = rail.ctl.now - w.touched > SLOW_AFTER_MS;
  return (
    <p className="ncx-live" data-state="listening" role="status" aria-live="polite">
      <StateDot state="waiting" />
      <span>{making === "making" || !w.conn ? "getting your connection ready" : slow ? `still listening for ${toolWord(t)}` : `listening for ${toolWord(t)}’s first call`}</span>
    </p>
  );
}

// ── the standing instruction ──────────────────────────────────────────────

function InstructionBody({ w, t, conn }: { w: Walk; t: ConnectTool; conn: Connection | null }) {
  const rail = useRail()!;
  const shot = t.guide?.instructions?.[0] ?? null;
  const listening = w.heardFrom !== null && !w.heard;
  return (
    <div className="ncx-body">
      {shot && <Shot shot={shot} t={t} />}
      <CopyBlock text={PROMPT_SYNC} className="copy-block--soft ncx-block ncx-block--long" />
      <p className="ncx-where">it goes in {t.promptWhere}</p>
      {listening ? (
        <p className="ncx-live" data-state="listening" role="status" aria-live="polite">
          <StateDot state="waiting" />
          <span>watching for a new chat in {appWord(t)} to reach for your memory</span>
        </p>
      ) : w.heard ? (
        <p className="ncx-live" data-state="heard" role="status">
          <StateDot state="live" />
          <span>it reached for your memory {conn?.last_seen_at ? relTime(conn.last_seen_at) : "just now"}</span>
        </p>
      ) : (
        <div className="ncx-act">
          <PillButton type="button" variant="primary" style={{ width: "auto" }} onClick={() => rail.ctl.pasted(w.id)}>
            i&rsquo;ve pasted it
          </PillButton>
        </div>
      )}
    </div>
  );
}

// ── let it work without asking ────────────────────────────────────────────

function TrustBody({ w, t }: { w: Walk; t: ConnectTool }) {
  const rail = useRail()!;
  const shot = t.guide?.permissions?.[0] ?? null;
  return (
    <div className="ncx-body">
      <ol className="ncx-steps">
        {(t.trust ?? []).map((s, i) => <li key={i}><span className="ncx-n">{i + 1}</span><span>{s}</span></li>)}
      </ol>
      {shot && <Shot shot={shot} t={t} />}
      <div className="ncx-act">
        <PillButton type="button" variant="primary" style={{ width: "auto" }} onClick={() => rail.ctl.advance(w.id)}>done</PillButton>
      </div>
    </div>
  );
}

// ── bring what it knows ───────────────────────────────────────────────────

function ImportBody({ w, t }: { w: Walk; t: ConnectTool }) {
  const rail = useRail()!;
  const steps = (t.importSteps ?? []).map((s) => s.replace(/bring it in with the button below/i, "then bring it in here"));
  return (
    <div className="ncx-body">
      <ol className="ncx-steps">
        {steps.map((s, i) => <li key={i}><span className="ncx-n">{i + 1}</span><span>{s}</span></li>)}
      </ol>
      <div className="ncx-act">
        <PillButton
          type="button"
          variant="primary"
          style={{ width: "auto" }}
          onClick={() => {
            window.dispatchEvent(new CustomEvent(NETTI_UI_EVENT, { detail: { open_import: true } }));
            rail.ctl.advance(w.id);
          }}
        >
          bring it in
        </PillButton>
      </div>
    </div>
  );
}

// ── done, and what next ───────────────────────────────────────────────────

function DoneBody({ w, t }: { w: Walk; t: ConnectTool }) {
  const rail = useRail()!;
  const have = new Set(rail.ctl.conns.filter((c) => c.last_seen_at).map((c) => c.tool_id));
  have.add(t.id);
  const next = suggestNext(have, 3);
  const steps = stepsFor(t).filter((s) => s !== "done" && s !== "name");
  return (
    <div className="ncx-body">
      <ul className="ncx-summary" aria-label="how it went">
        {steps.map((s) => {
          const skipped = w.skipped.includes(s) && !(s === "wire" && w.arrived);
          return (
            <li key={s} data-skipped={skipped || undefined}>
              {skipped ? <span className="ncx-dash" aria-hidden>–</span> : <Tick />}
              {STEP_WORD[s]}
            </li>
          );
        })}
      </ul>
      <p className="ncx-where">try it: ask {appWord(t)} in a new chat</p>
      <CopyBlock text={tryItAsk(t)} className="copy-block--soft ncx-block" />
      {next.length > 0 && (
        <div className="ncx-tools" role="group" aria-label="connect another">
          {next.map((n) => (
            <button key={n.id} type="button" className="ncx-tool" onClick={() => rail.ctl.start({ tool: n.id, from: "next" })}>
              <BrandIcon name={n.icon_key} size={18} />
              <span>{appWord(n)}</span>
            </button>
          ))}
          <button type="button" className="ncx-tool ncx-tool--more" onClick={() => rail.ctl.start({ from: "pick" })}>something else</button>
        </div>
      )}
    </div>
  );
}

// ── what to connect ───────────────────────────────────────────────────────

const PICK_FIRST = 8;

function PickCard() {
  const rail = useRail()!;
  const [all, setAll] = React.useState(false);
  const connected = rail.ctl.conns.filter((c) => c.last_seen_at);
  const have = connected.map((c) => c.tool_id);
  const tools = pickable(have);
  const shown = all ? tools : tools.slice(0, PICK_FIRST);
  const haveTools = [...new Set(have)].map((id) => walkTool(id)).filter((x): x is ConnectTool => !!x);
  return (
    <section className="ncx-card ncx-card--pick" aria-label="what to connect">
      {haveTools.length > 0 && (
        <p className="ncx-have" aria-label="already connected">
          {haveTools.map((h) => <BrandIcon key={h.id} name={h.icon_key} size={16} title={`${toolWord(h)}: connected`} />)}
          <span>already connected</span>
        </p>
      )}
      <div className="ncx-tools ncx-tools--grid">
        {shown.map((t) => (
          <button key={t.id} type="button" className="ncx-tool" onClick={() => rail.ctl.focusTool(t.id)} title={t.note}>
            <BrandIcon name={t.icon_key} size={18} />
            <span>{appWord(t)}</span>
          </button>
        ))}
        {tools.length > PICK_FIRST && !all ? (
          <button type="button" className="ncx-tool ncx-tool--more" onClick={() => setAll(true)}>{tools.length - PICK_FIRST} more</button>
        ) : (
          <button type="button" className="ncx-tool ncx-tool--more" onClick={() => rail.ctl.start({ tool: "other", from: "pick" })}>any mcp app</button>
        )}
      </div>
    </section>
  );
}

// ── small parts ───────────────────────────────────────────────────────────

const Tick = () => (
  <svg className="ncx-tick" width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden>
    <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Pencil = () => (
  <svg className="ncx-pencil" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M10.6 2.6a1.6 1.6 0 0 1 2.3 2.3L5.6 12.2 2.5 13l.8-3.1 7.3-7.3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

/** the mark beside each way in */
function WayMark({ way }: { way: WalkWay }) {
  if (way === "plugin") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M6 2.5v3M10 2.5v3M4.5 5.5h7v2.5a3.5 3.5 0 0 1-7 0V5.5ZM8 11.5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (way === "npx") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="m3.5 5 3 3-3 3M8.5 11.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (way === "coach") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M6.5 2.5h3v2a1.5 1.5 0 1 0 3 0v-2h1v11h-11v-11h1v2a1.5 1.5 0 1 0 3 0v-2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3 4.5h10M3 8h7M3 11.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** the shared link, on THE link bar (one bar, everywhere a link is handed over) */
function LinkCopy({ url }: { url: string }) {
  const [done, setDone] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (
    <div className="ncx-link">
      <LinkBar
        url={url}
        size="compact"
        shown={url.replace(/^https:\/\//, "")}
        done={done}
        label="copy the link"
        onCopy={() => {
          void copyText(url).then((ok) => {
            setDone(ok);
            if (ok) trackFunnel(EVENTS.connect_instructions_copied, { what: "connection_endpoint", flow: FLOW });
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setDone(false), 1800);
          });
        }}
      />
    </div>
  );
}

/** the one real screen for a step, the press marked: the whole picture, or the part a crop frames, and a
 *  moving clip of the same moment where there is one. A press opens it whole in THE media viewer (the
 *  blog's own, components/company/MediaModal), over the page, never a new tab (John, 2026-10-29). */
function Shot({ shot, t }: { shot: GuideShot; t: ConnectTool }) {
  const [open, setOpen] = React.useState(false);
  const still = useReducedMotion();
  const [w, h] = shot.size ?? [1600, 880];
  const c = shot.crop;
  const video = !!shot.clip && /\.(mp4|webm)$/i.test(shot.clip);
  // a gif is a picture that moves: it stands in for the still, unless motion is asked to stay still
  const pic = shot.clip && !video && !still ? shot.clip : shot.src;
  // a crop: the frame takes the cropped part's shape, and the picture is laid inside it so that part fills it
  const frame: React.CSSProperties = { aspectRatio: c ? `${c.w * w} / ${c.h * h}` : `${w} / ${h}` };
  const place: React.CSSProperties | undefined = c
    ? { position: "absolute", width: `${100 / c.w}%`, maxWidth: "none", left: `${(-c.x / c.w) * 100}%`, top: `${(-c.y / c.h) * 100}%` }
    : undefined;
  return (
    <>
      <button type="button" className="ncx-shot" data-crop={c ? "" : undefined} style={frame} onClick={() => setOpen(true)} aria-label={`${shot.alt}, larger`}>
        {video && !still ? (
          <video src={shot.clip} poster={shot.src} autoPlay muted loop playsInline style={place} aria-hidden />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={pic} alt={shot.alt} width={w} height={h} loading="lazy" decoding="async" draggable={false} style={place} />
        )}
      </button>
      {open && (
        <MediaModal
          media={[video ? { type: "video", src: shot.clip!, poster: shot.src, alt: shot.alt, context: shot.alt } : { type: "image", src: pic, alt: shot.alt, context: shot.alt }]}
          initialIndex={0}
          crumb={["connections", toolWord(t)]}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** does this reader ask for less motion? (read after mount, so the server and the first paint agree) */
function useReducedMotion(): boolean {
  const [still, setStill] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const read = () => setStill(mq.matches);
    read();
    mq.addEventListener("change", read);
    return () => mq.removeEventListener("change", read);
  }, []);
  return still;
}
